import { Order } from "./types";

export const uniqSorted = (values: string[]) =>
  Array.from(new Set(values)).filter(Boolean).sort();

export function getUniqueBrands(orders: Order[]) {
  return uniqSorted(orders.map((o) => o.brand));
}

export function getUniqueLocations(orders: Order[]) {
  return uniqSorted(orders.map((o) => o.location));
}

export function getUniqueStatuses(orders: Order[]) {
  return uniqSorted(orders.map((o) => String(o.status ?? "").trim()));
}

/**
 * Age is currently typed as string in Order.
 * We expect it to usually be numeric minutes, e.g. "38".
 */
function parseAgeMinutes(age: unknown): number | null {
  if (age === null || age === undefined) return null;
  const raw = String(age).trim();
  if (!raw) return null;

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * riskPercent is currently typed as string in Order.
 * Supports: "80", "80%", "0.8" (ratio), and number equivalents.
 */
function parseRiskPercent(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (raw.endsWith("%")) {
    const n = Number(raw.slice(0, -1));
    return Number.isFinite(n) ? n : null;
  }

  const n = Number(raw);
  if (!Number.isFinite(n)) return null;

  // If 0..1 treat as ratio (0.82 => 82)
  if (n > 0 && n <= 1) return n * 100;

  return n;
}

export function filterOrders(
  orders: Order[],
  opts: {
    search: string;
    brand: string; // "ALL" or value
    location: string; // "ALL" or value
    minAge: string; // "ALL" or numeric string (minutes)
    minRiskPercent: string; // "ALL" or numeric string (percent)

    // Multi-select statuses. If empty/undefined => no status filtering.
    statuses?: string[];
  }
) {
  const s = opts.search.trim().toLowerCase();

  const minAgeNum = opts.minAge === "ALL" ? null : Number(opts.minAge);
  const minRiskNum =
    opts.minRiskPercent === "ALL" ? null : Number(opts.minRiskPercent);

  const selectedStatuses = new Set(
    (opts.statuses ?? []).map((x) => String(x).trim()).filter(Boolean)
  );

  return orders.filter((o) => {
    const matchesSearch =
      s.length === 0 ||
      o.id.includes(opts.search) ||
      o.customer.toLowerCase().includes(s) ||
      o.brand.toLowerCase().includes(s);

    const matchesBrand = opts.brand === "ALL" || o.brand === opts.brand;

    const matchesLocation =
      opts.location === "ALL" || o.location === opts.location;

    const matchesMinAge =
      minAgeNum === null
        ? true
        : (parseAgeMinutes(o.age) ?? -Infinity) >= minAgeNum;

    const matchesMinRisk =
      minRiskNum === null
        ? true
        : (parseRiskPercent(o.riskPercent) ?? -Infinity) >= minRiskNum;

    const orderStatus = String(o.status ?? "").trim();
    const matchesStatus =
      selectedStatuses.size === 0 ? true : selectedStatuses.has(orderStatus);

    return (
      matchesSearch &&
      matchesBrand &&
      matchesLocation &&
      matchesMinAge &&
      matchesMinRisk &&
      matchesStatus
    );
  });
}
